// ============================================================================
// PROJECT GRAPH WIDGET
// Animated display-only knowledge graph for the second screen.
// Features: gentle continuous drift, particles along edges, type legend overlay.
// Reuses all canvas helpers from customerResearchGraphHelpers.
// ============================================================================

import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Network } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';
import {
  getNodeConfig,
  getEdgeColor,
  getLinkEndpointId,
  drawNode,
  drawNodeHitArea,
  drawTooltip,
  drawEdgeLabel,
  DrawNodeOpts,
} from '../../project/customerResearchGraphHelpers';

// ---------------------------------------------------------------------------
// Theme colours for canvas-drawn elements
// ---------------------------------------------------------------------------

interface GraphTheme {
  bg: string;
  legendBg: string;
  legendBorder: string;
  legendText: string;
  dimmedLink: string;
}

const DARK_THEME: GraphTheme = {
  bg: '#0f172a',
  legendBg: 'rgba(15, 23, 42, 0.82)',
  legendBorder: '#334155',
  legendText: '#94a3b8',
  dimmedLink: 'rgba(71, 85, 105, 0.12)',
};

const LIGHT_THEME: GraphTheme = {
  bg: '#f8fafc',
  legendBg: 'rgba(255, 255, 255, 0.9)',
  legendBorder: '#cbd5e1',
  legendText: '#475569',
  dimmedLink: 'rgba(148, 163, 184, 0.18)',
};

// ---------------------------------------------------------------------------
// Type Legend — drawn in screen-space on the canvas
// ---------------------------------------------------------------------------

function drawTypeLegend(ctx: CanvasRenderingContext2D, nodes: any[], theme: GraphTheme): void {
  const typesPresent = new Set<string>();
  nodes.forEach(n => typesPresent.add(n.type));
  if (typesPresent.size === 0) return;

  const types = Array.from(typesPresent).sort();
  const fontSize = 11;
  const lineHeight = 18;
  const dotRadius = 4;
  const padX = 12;
  const padY = 10;
  const gapDotText = 8;

  ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;

  let maxTextWidth = 0;
  for (const type of types) {
    const config = getNodeConfig(type);
    const w = ctx.measureText(config.label).width;
    if (w > maxTextWidth) maxTextWidth = w;
  }

  const legendW = padX * 2 + dotRadius * 2 + gapDotText + maxTextWidth;
  const legendH = padY * 2 + types.length * lineHeight;
  const legendX = 10;
  const legendY = ctx.canvas.height - legendH - 10;

  // Background
  ctx.fillStyle = theme.legendBg;
  ctx.strokeStyle = theme.legendBorder;
  ctx.lineWidth = 1;
  const r = 6;
  ctx.beginPath();
  ctx.moveTo(legendX + r, legendY);
  ctx.lineTo(legendX + legendW - r, legendY);
  ctx.quadraticCurveTo(legendX + legendW, legendY, legendX + legendW, legendY + r);
  ctx.lineTo(legendX + legendW, legendY + legendH - r);
  ctx.quadraticCurveTo(legendX + legendW, legendY + legendH, legendX + legendW - r, legendY + legendH);
  ctx.lineTo(legendX + r, legendY + legendH);
  ctx.quadraticCurveTo(legendX, legendY + legendH, legendX, legendY + legendH - r);
  ctx.lineTo(legendX, legendY + r);
  ctx.quadraticCurveTo(legendX, legendY, legendX + r, legendY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Entries
  types.forEach((type, i) => {
    const config = getNodeConfig(type);
    const entryY = legendY + padY + i * lineHeight + lineHeight / 2;

    // Colored dot
    ctx.beginPath();
    ctx.arc(legendX + padX + dotRadius, entryY, dotRadius, 0, Math.PI * 2);
    ctx.fillStyle = config.color;
    ctx.fill();

    // Label
    ctx.fillStyle = theme.legendText;
    ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(config.label, legendX + padX + dotRadius * 2 + gapDotText, entryY);
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProjectGraphWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const graphTheme = isDark ? DARK_THEME : LIGHT_THEME;
  const graphData = state?.graphData ?? null;
  const graphRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoveredNodeRef = useRef<any>(null);
  const highlightedNeighboursRef = useRef<Set<string>>(new Set());
  const highlightedLinksRef = useRef<Set<string>>(new Set());
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 });

  // ResizeObserver for container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      setDimensions({ width: el.clientWidth, height: el.clientHeight });
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Computed: connection counts per node
  const connectionCounts = useMemo(() => {
    if (!graphData) return new Map<string, number>();
    const counts = new Map<string, number>();
    graphData.edges.forEach(edge => {
      counts.set(edge.source, (counts.get(edge.source) || 0) + 1);
      counts.set(edge.target, (counts.get(edge.target) || 0) + 1);
    });
    return counts;
  }, [graphData]);

  // Prepared data for ForceGraph2D
  const forceGraphData = useMemo(() => {
    if (!graphData) return { nodes: [], links: [] };
    const nodes = graphData.nodes.map(node => ({
      ...node,
      __connections: connectionCounts.get(node.id) || 0,
    }));
    const nodeIds = new Set(nodes.map(n => n.id));
    const links = graphData.edges
      .filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target))
      .map(edge => ({
        source: edge.source,
        target: edge.target,
        type: edge.type,
        id: edge.id,
      }));
    return { nodes, links };
  }, [graphData, connectionCounts]);

  // Configure forces for gentle continuous drift
  useEffect(() => {
    if (!graphRef.current || forceGraphData.nodes.length === 0) return;

    const fg = graphRef.current;

    // Weaker charge for gentler repulsion
    const charge = fg.d3Force('charge');
    if (charge) charge.strength(-80);

    // Type clustering force
    fg.d3Force('cluster', (alpha: number) => {
      const nodes = forceGraphData.nodes as any[];
      const centroids = new Map<string, { x: number; y: number; count: number }>();
      for (const n of nodes) {
        if (n.x === undefined) continue;
        const c = centroids.get(n.type) || { x: 0, y: 0, count: 0 };
        c.x += n.x; c.y += n.y; c.count++;
        centroids.set(n.type, c);
      }
      centroids.forEach(c => { c.x /= c.count; c.y /= c.count; });
      const strength = 0.025 * alpha;
      for (const n of nodes) {
        const c = centroids.get(n.type);
        if (!c || n.x === undefined) continue;
        n.vx = (n.vx || 0) + (c.x - n.x) * strength;
        n.vy = (n.vy || 0) + (c.y - n.y) * strength;
      }
    });

    fg.d3ReheatSimulation();
  }, [forceGraphData]);


  // Zoom-to-fit after data loads
  useEffect(() => {
    if (forceGraphData.nodes.length > 0) {
      setTimeout(() => graphRef.current?.zoomToFit(600, 40), 500);
    }
  }, [forceGraphData.nodes.length]);

  // Hover handler — highlight neighbourhood
  const handleNodeHover = useCallback((node: any | null) => {
    hoveredNodeRef.current = node || null;
    if (node) {
      const neighbours = new Set<string>([node.id]);
      const links = new Set<string>();
      forceGraphData.links.forEach(link => {
        const srcId = getLinkEndpointId(link.source);
        const tgtId = getLinkEndpointId(link.target);
        if (srcId === node.id || tgtId === node.id) {
          neighbours.add(srcId);
          neighbours.add(tgtId);
          links.add(link.id);
        }
      });
      highlightedNeighboursRef.current = neighbours;
      highlightedLinksRef.current = links;
    } else {
      highlightedNeighboursRef.current = new Set();
      highlightedLinksRef.current = new Set();
    }
  }, [forceGraphData.links]);

  // Canvas rendering callbacks (reuse helpers directly)
  const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const hNeighbours = highlightedNeighboursRef.current;
    const opts: DrawNodeOpts = {
      isSelected: false,
      isHovered: hoveredNodeRef.current?.id === node.id,
      isDimmed: hNeighbours.size > 0 && !hNeighbours.has(node.id),
    };
    drawNode(node, ctx, globalScale, opts);
  }, []);

  const nodePointerAreaPaint = useCallback((node: any, color: string, ctx: CanvasRenderingContext2D) => {
    drawNodeHitArea(node, color, ctx);
  }, []);

  const linkColorFn = useCallback((link: any) => {
    const hLinks = highlightedLinksRef.current;
    if (hLinks.size > 0 && !hLinks.has(link.id)) return graphTheme.dimmedLink;
    return getEdgeColor(link.type);
  }, [graphTheme.dimmedLink]);

  const linkWidthFn = useCallback((link: any) => {
    const hLinks = highlightedLinksRef.current;
    return (hLinks.size > 0 && hLinks.has(link.id)) ? 2.5 : 1.2;
  }, []);

  const linkParticleColorFn = useCallback((link: any) => {
    return getEdgeColor(link.type) + '99';
  }, []);

  const linkCanvasObject = useCallback((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const hLinks = highlightedLinksRef.current;
    const isDimmed = hLinks.size > 0 && !hLinks.has(link.id);
    drawEdgeLabel(link, ctx, globalScale, isDimmed);
  }, []);

  // Post-render: tooltip + type legend
  const handleRenderFramePost = useCallback((ctx: CanvasRenderingContext2D, globalScale: number) => {
    // Tooltip in graph-space
    const hovered = hoveredNodeRef.current;
    if (hovered && hovered.x !== undefined) {
      drawTooltip(hovered, ctx, globalScale);
    }

    // Type legend in screen-space (bottom-left)
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    drawTypeLegend(ctx, forceGraphData.nodes, graphTheme);
    ctx.restore();
  }, [forceGraphData.nodes, graphTheme]);

  // Empty state
  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div ref={containerRef} className={`flex flex-col items-center justify-center h-full gap-3 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        <Network size={32} className="animate-pulse" />
        <p className="text-sm font-medium">Waiting for Knowledge Graph...</p>
        <p className="text-xs opacity-60">Run research tasks to populate</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full">
      <ForceGraph2D
        ref={graphRef}
        graphData={forceGraphData}
        backgroundColor={graphTheme.bg}
        width={dimensions.width}
        height={dimensions.height}
        // Node rendering
        nodeCanvasObject={nodeCanvasObject}
        nodeCanvasObjectMode={() => 'replace' as any}
        nodePointerAreaPaint={nodePointerAreaPaint}
        // Link rendering
        linkColor={linkColorFn}
        linkWidth={linkWidthFn}
        linkCurvature={0.2}
        linkCanvasObject={linkCanvasObject}
        linkCanvasObjectMode={() => 'after' as any}
        linkDirectionalArrowLength={5}
        linkDirectionalArrowRelPos={0.95}
        linkDirectionalArrowColor={linkColorFn}
        // Particles
        linkDirectionalParticles={2}
        linkDirectionalParticleSpeed={0.003}
        linkDirectionalParticleColor={linkParticleColorFn}
        linkDirectionalParticleWidth={2}
        // Interaction (hover only — display-only widget)
        onNodeHover={handleNodeHover}
        onRenderFramePost={handleRenderFramePost}
        enableNodeDrag={false}
        // Let simulation settle then stop
        d3AlphaDecay={0.05}
        d3VelocityDecay={0.4}
        cooldownTime={3000}
      />
    </div>
  );
};

export default ProjectGraphWidget;
